import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useCallback, useMemo, useContext, createContext } from 'react';
import { ConfirmDialog } from './components/ConfirmDialog.js';
import { DefaultDialog } from './components/DefaultDialog.js';
import { FormDialog } from './components/FormDialog.js';
import { ModalDialog } from './components/ModalDialog.js';
import { StepperDialog } from './components/StepperDialog.js';
import { DialogType } from './types.js';
import { DialogSize } from './constraints.js';
import 'primereact/button';
import 'primereact/dialog';
import 'tslib';
import 'react-router-dom';
import 'primereact/utils';
import 'clsx';

const emptyDialogState = {};
const DialogContext = /*#__PURE__*/createContext({
  open: () => null,
  close: () => null,
  confirm: () => null,
  form: () => null,
  modal: () => null,
  stepper: () => null,
  loading: false,
  handleSubmit: () => null,
  setDisabled: () => {},
  disabled: false,
  dialogState: emptyDialogState,
  setDialogState: () => {}
});
function DialogProvider(_ref) {
  let {
    children
  } = _ref;
  const [dialogConfig, setDialogConfig] = useState(null);
  const [dialogInstanceKey, setDialogInstanceKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const submitRef = useRef(undefined);
  const [disabled, setDisabled] = useState(false);
  const [dialogState, setDialogState] = useState(emptyDialogState);
  const dialogConfigRef = useRef(dialogConfig);
  dialogConfigRef.current = dialogConfig;
  const onSubmit = useCallback(() => {
    const submit = submitRef.current;
    if (!submit) {
      return;
    }
    setLoading(true);
    submit(dialogConfigRef.current).finally(() => setLoading(false));
  }, []);
  const applyDefaultConfig = useCallback((config, type) => {
    setDisabled(false);
    setDialogState(emptyDialogState);
    setDialogInstanceKey(current => current + 1);
    setDialogConfig(Object.assign(Object.assign({}, config), {
      type
    }));
  }, []);
  const openDialog = useCallback(config => {
    applyDefaultConfig(config, DialogType.Default);
  }, [applyDefaultConfig]);
  const openModal = useCallback(config => {
    applyDefaultConfig(config, DialogType.Modal);
  }, [applyDefaultConfig]);
  const openForm = useCallback(config => {
    setLoading(false);
    applyDefaultConfig(config, DialogType.Form);
  }, [applyDefaultConfig]);
  const openConfirm = useCallback(config => {
    setLoading(false);
    applyDefaultConfig(config, DialogType.Confirm);
  }, [applyDefaultConfig]);
  const openStepper = useCallback(config => {
    setLoading(false);
    setDisabled(false);
    setDialogState(emptyDialogState);
    setDialogInstanceKey(current => current + 1);
    setDialogConfig(Object.assign(Object.assign({}, config), {
      type: DialogType.Stepper
    }));
  }, []);
  const closeDialog = useCallback(() => {
    setLoading(false);
    submitRef.current = undefined;
    setDialogConfig(null);
    setDialogState(emptyDialogState);
  }, []);
  const handleSubmit = useCallback(callback => {
    submitRef.current = callback;
  }, []);
  const dialog = useMemo(() => {
    if (!dialogConfig) {
      return null;
    }
    const {
      content,
      title,
      subtitle,
      type,
      size,
      buttons,
      dialogClassName
    } = dialogConfig;
    switch (type) {
      case DialogType.Confirm:
        return jsx(ConfirmDialog, {
          title: title,
          buttons: buttons,
          onClose: closeDialog,
          loading: loading,
          onSubmit: onSubmit,
          children: content
        }, dialogInstanceKey);
      case DialogType.Form:
        return jsx(FormDialog, {
          title: title,
          subtitle: subtitle,
          buttons: buttons,
          onClose: closeDialog,
          size: size !== null && size !== void 0 ? size : DialogSize.md,
          disabled: disabled,
          loading: loading,
          onSubmit: onSubmit,
          pt: dialogConfig.pt,
          confirmClose: dialogConfig.confirmClose,
          dialogClassName: dialogClassName,
          children: content
        }, dialogInstanceKey);
      case DialogType.Modal:
        return jsx(ModalDialog, {
          dialogClassName: dialogClassName,
          title: title,
          buttons: buttons,
          size: size,
          onClose: closeDialog,
          pt: dialogConfig.pt,
          children: content
        }, dialogInstanceKey);
      case DialogType.Default:
        return jsx(DefaultDialog, {
          title: title,
          buttons: buttons,
          onClose: closeDialog,
          children: content
        }, dialogInstanceKey);
      case DialogType.Stepper:
        {
          const stepperConfig = dialogConfig;
          return jsx(StepperDialog, {
            steps: stepperConfig.steps,
            title: title,
            subtitle: subtitle,
            allStepsReached: stepperConfig.allStepsReached,
            linear: stepperConfig.linear,
            onClose: closeDialog,
            size: size,
            loading: loading,
            setLoading: setLoading,
            pt: dialogConfig.pt,
            buttons: buttons,
            dialogClassName: dialogClassName,
            wrapper: stepperConfig.wrapper,
            confirmClose: stepperConfig.confirmClose
          }, dialogInstanceKey);
        }
      default:
        return null;
    }
  }, [dialogConfig, dialogInstanceKey, disabled, loading, onSubmit, closeDialog]);
  const contextValue = useMemo(() => ({
    open: openDialog,
    close: closeDialog,
    form: openForm,
    confirm: openConfirm,
    modal: openModal,
    stepper: openStepper,
    loading,
    handleSubmit,
    setDisabled,
    disabled,
    onSubmit,
    dialogState,
    setDialogState
  }), [openDialog, closeDialog, openForm, openConfirm, openModal, openStepper, loading, handleSubmit, disabled, onSubmit, dialogState]);
  return jsxs(DialogContext.Provider, {
    value: contextValue,
    children: [children, dialog]
  });
}
function useDialog() {
  return useContext(DialogContext);
}

export { DialogProvider, useDialog };
